import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import DirectionSnackbar from '../../../Components/Snackbar'
import {
  makeGetRequest,
  makePostRequest,
  makePutRequest,
  makeDeleteRequest,
} from 'utils/api'

/**
 * LAP-769 — "Paramétrer mes contraintes".
 *
 * Écran de configuration des contraintes de vie utilisées par le générateur de
 * planning : horaires de travail, routine matinale, sport, chien, famille, social,
 * tâches, « travailler sur soi ». CRUD sur /api/time/planning/constraints/ (DRF
 * ModelViewSet) + bouton « Initialiser par défaut » (seed-defaults).
 *
 * Expose handleClickOpen() via ref (même contrat que GeneratePlanningModal).
 */

const DOMAINS = [
  'work',
  'routine',
  'sport',
  'dog',
  'family',
  'meal',
  'sleep',
  'social',
  'task',
  'chore',
  'errand',
  'goal',
  'personal',
  'other',
]

const DOMAIN_COLORS = {
  work: '#1b75d0',
  routine: '#d39a3a',
  sport: '#2e9e6b',
  dog: '#c98a2b',
  family: '#9b59b6',
  meal: '#e0734a',
  sleep: '#5a6b7b',
  social: '#3aa6a0',
  task: '#6d7fd6',
  chore: '#8a7d68',
  errand: '#b1684a',
  goal: '#3f7e4e',
  personal: '#c0567f',
  other: '#7c8a99',
}

const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

// LAP-771 — périodicité de la contrainte. "weekly" reproduit le comportement
// historique (N séances chaque semaine) ; les autres espacent les occurrences
// selon la dernière planification (last_scheduled_on).
const PERIODICITIES = ['weekly', 'biweekly', 'monthly', 'quarterly', 'yearly']

const EMPTY_FORM = {
  id: null,
  domain: 'work',
  label: '',
  block_kind: 'fixed',
  periodicity: 'weekly',
  sessions_per_week: 1,
  duration_min: 60,
  start_time: '09:00',
  end_time: '17:00',
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
  sunday: false,
  priority: 100,
  active: true,
}

// Le backend renvoie l'heure en "HH:MM:SS" ; l'input type=time veut "HH:MM".
const trimTime = (v) => (v ? String(v).slice(0, 5) : '')

const ConstraintsModal = React.forwardRef((props, ref) => {
  const { onChange } = props
  const { t } = useTranslation('layout')
  const tr = (k, dflt, opts) =>
    t(`time.planning.settings.${k}`, { defaultValue: dflt, ...(opts || {}) })
  const trDomain = (d) =>
    t(`time.planning.domain.${d}`, { defaultValue: d.charAt(0).toUpperCase() + d.slice(1) })
  // LAP-771 — libellés de périodicité.
  const PERIOD_LABELS = {
    weekly: tr('periodWeekly', 'Hebdomadaire'),
    biweekly: tr('periodBiweekly', 'Toutes les 2 semaines'),
    monthly: tr('periodMonthly', 'Mensuel'),
    quarterly: tr('periodQuarterly', 'Trimestriel'),
    yearly: tr('periodYearly', 'Annuel'),
  }
  // "par semaine" / "par mois" / "par trimestre" / "par an" pour le libellé des séances.
  const PERIOD_PER = {
    weekly: tr('perWeek', 'par semaine'),
    biweekly: tr('perBiweek', 'par quinzaine'),
    monthly: tr('perMonth', 'par mois'),
    quarterly: tr('perQuarter', 'par trimestre'),
    yearly: tr('perYear', 'par an'),
  }

  const [open, setOpen] = React.useState(false)
  const [view, setView] = React.useState('list') // 'list' | 'form'
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [items, setItems] = React.useState([])
  const [form, setForm] = React.useState(EMPTY_FORM)
  const [snack, setSnack] = React.useState({ open: false, color: 'info', message: '' })

  const handleClickOpen = () => {
    setOpen(true)
    setView('list')
    fetchItems()
  }
  const handleClose = () => {
    if (saving) return
    setOpen(false)
    if (typeof onChange === 'function') onChange()
  }
  React.useImperativeHandle(ref, () => ({ handleClickOpen }))

  const showSnack = (color, message) => {
    setSnack({ open: true, color, message })
    setTimeout(() => setSnack({ open: false, color, message: '' }), 3500)
  }

  const fetchItems = async () => {
    setLoading(true)
    const res = await makeGetRequest('/api/time/planning/constraints/', {}, true)
    setLoading(false)
    if (res && res.status === 200) {
      const raw = res.data
      const list = Array.isArray(raw) ? raw : Array.isArray(raw?.results) ? raw.results : []
      setItems(list)
    } else {
      showSnack('error', tr('errorGeneric', 'Une erreur est survenue.'))
    }
  }

  const openCreate = () => {
    setForm(EMPTY_FORM)
    setView('form')
  }
  const openEdit = (it) => {
    setForm({
      ...EMPTY_FORM,
      ...it,
      start_time: trimTime(it.start_time) || '09:00',
      end_time: trimTime(it.end_time) || '17:00',
    })
    setView('form')
  }

  const setF = (patch) => setForm((prev) => ({ ...prev, ...patch }))

  // Durée saisie en heures + minutes, mais toujours stockée en minutes (DB).
  const durH = Math.floor((Number(form.duration_min) || 0) / 60)
  const durM = (Number(form.duration_min) || 0) % 60
  const setDuration = (h, m) => {
    const total = Math.max(0, (Number(h) || 0) * 60 + (Number(m) || 0))
    setF({ duration_min: total })
  }

  const handleSave = async () => {
    if (!form.label.trim()) {
      showSnack('warning', tr('labelRequired', 'Donnez un nom à la contrainte.'))
      return
    }
    setSaving(true)
    const payload = {
      domain: form.domain,
      label: form.label.trim(),
      block_kind: form.block_kind,
      periodicity: form.periodicity || 'weekly',
      sessions_per_week: Number(form.sessions_per_week) || 1,
      duration_min: Number(form.duration_min) || 60,
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      monday: form.monday,
      tuesday: form.tuesday,
      wednesday: form.wednesday,
      thursday: form.thursday,
      friday: form.friday,
      saturday: form.saturday,
      sunday: form.sunday,
      priority: Number(form.priority) || 100,
      active: form.active,
    }
    const res = form.id
      ? await makePutRequest(`/api/time/planning/constraints/${form.id}/`, payload, {}, true)
      : await makePostRequest('/api/time/planning/constraints/', payload, {}, true)
    setSaving(false)
    if (res && (res.status === 200 || res.status === 201)) {
      setView('list')
      fetchItems()
      showSnack('success', tr('saved', 'Contrainte enregistrée.'))
    } else {
      showSnack('error', (res && res.data && JSON.stringify(res.data)) || tr('errorGeneric', 'Une erreur est survenue.'))
    }
  }

  const handleDelete = async (it) => {
    const res = await makeDeleteRequest(`/api/time/planning/constraints/${it.id}/`, {}, true)
    if (res && (res.status === 204 || res.status === 200)) {
      fetchItems()
      showSnack('success', tr('deleted', 'Contrainte supprimée.'))
    } else {
      showSnack('error', tr('errorGeneric', 'Une erreur est survenue.'))
    }
  }

  const handleSeed = async () => {
    setSaving(true)
    const res = await makePostRequest('/api/time/planning/constraints/seed-defaults/', {}, {}, true)
    setSaving(false)
    if (res && res.status === 200) {
      fetchItems()
      showSnack('success', tr('seeded', '{{count}} contraintes par défaut créées.', { count: res.data.created }))
    } else if (res && res.status === 409) {
      showSnack('info', tr('alreadySeeded', 'Vous avez déjà des contraintes configurées.'))
    } else {
      showSnack('error', tr('errorGeneric', 'Une erreur est survenue.'))
    }
  }

  const daysSummary = (it) => {
    const map = { monday: 'L', tuesday: 'Ma', wednesday: 'Me', thursday: 'J', friday: 'V', saturday: 'S', sunday: 'D' }
    const on = WEEKDAYS.filter((d) => it[d]).map((d) => map[d])
    return on.length === 7 ? tr('everyDay', 'Tous les jours') : on.join(' ')
  }

  // "90" -> "1 h 30", "120" -> "2 h", "45" -> "45 min".
  const fmtDuration = (min) => {
    const m = Number(min) || 0
    const h = Math.floor(m / 60)
    const r = m % 60
    if (h && r) return `${h} h ${r}`
    if (h) return `${h} h`
    return `${r} min`
  }

  // LAP-771 — abréviation de la période pour le résumé (×/sem, ×/mois…).
  const PERIOD_ABBR = {
    weekly: tr('abbrWeek', 'sem'),
    biweekly: tr('abbrBiweek', 'quinz'),
    monthly: tr('abbrMonth', 'mois'),
    quarterly: tr('abbrQuarter', 'trim'),
    yearly: tr('abbrYear', 'an'),
  }

  const slotSummary = (it) => {
    const per = PERIOD_ABBR[it.periodicity || 'weekly']
    if (it.block_kind === 'fixed') {
      const slot = `${trimTime(it.start_time)}–${trimTime(it.end_time)}`
      // Pour une contrainte non hebdomadaire, on précise la périodicité.
      return (it.periodicity && it.periodicity !== 'weekly')
        ? `${slot} · ${PERIOD_LABELS[it.periodicity]}`
        : slot
    }
    return tr('flexSummary', '{{n}}×/{{per}} · {{d}} · {{from}}–{{to}}', {
      n: it.sessions_per_week,
      per,
      d: fmtDuration(it.duration_min),
      from: trimTime(it.start_time) || '—',
      to: trimTime(it.end_time) || '—',
    })
  }

  return (
    <>
      {snack.open && <DirectionSnackbar message={snack.message} color={snack.color} />}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneIcon fontSize="small" />
          {tr('title', 'Paramétrer mes contraintes')}
        </DialogTitle>
        <DialogContent dividers>
          {view === 'form' ? (
            <Stack spacing={2.2}>
              <Typography variant="caption" color="text.secondary">
                {tr(
                  'formHint',
                  'Un bloc « fixe » occupe un créneau précis (ex. journée de travail). Un bloc « flexible » est placé automatiquement dans la fenêtre choisie.',
                )}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={tr('fieldDomain', 'Domaine')}
                  value={form.domain}
                  onChange={(e) => setF({ domain: e.target.value })}
                >
                  {DOMAINS.map((d) => (
                    <MenuItem key={d} value={d}>
                      {trDomain(d)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  size="small"
                  label={tr('fieldLabel', 'Nom du bloc')}
                  value={form.label}
                  onChange={(e) => setF({ label: e.target.value })}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={tr('fieldKind', 'Type')}
                  value={form.block_kind}
                  onChange={(e) => setF({ block_kind: e.target.value })}
                >
                  <MenuItem value="fixed">{tr('kindFixed', 'Fixe (créneau précis)')}</MenuItem>
                  <MenuItem value="flexible">{tr('kindFlexible', 'Flexible (placé auto.)')}</MenuItem>
                </TextField>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={tr('fieldPeriodicity', 'Périodicité')}
                  value={form.periodicity || 'weekly'}
                  onChange={(e) => setF({ periodicity: e.target.value })}
                  helperText={tr(
                    'periodicityHint',
                    'À quelle fréquence répéter cette contrainte.',
                  )}
                >
                  {PERIODICITIES.map((p) => (
                    <MenuItem key={p} value={p}>
                      {PERIOD_LABELS[p]}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={tr('fieldPriority', 'Priorité (placé en premier si bas)')}
                  value={form.priority}
                  onChange={(e) => setF({ priority: e.target.value })}
                />
              </Stack>

              {form.block_kind === 'flexible' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={tr('fieldSessions', 'Séances {{per}}', {
                      per: PERIOD_PER[form.periodicity || 'weekly'],
                    })}
                    value={form.sessions_per_week}
                    onChange={(e) => setF({ sessions_per_week: e.target.value })}
                  />
                  <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      label={tr('fieldDurationHours', 'Durée — heures')}
                      value={durH}
                      onChange={(e) => setDuration(e.target.value, durM)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      inputProps={{ min: 0, max: 59, step: 5 }}
                      label={tr('fieldDurationMinutes', 'Durée — minutes')}
                      value={durM}
                      onChange={(e) => setDuration(durH, e.target.value)}
                    />
                  </Stack>
                </Stack>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="time"
                  label={
                    form.block_kind === 'fixed'
                      ? tr('fieldStart', 'Début')
                      : tr('fieldWindowStart', 'Fenêtre — de')
                  }
                  InputLabelProps={{ shrink: true }}
                  value={form.start_time}
                  onChange={(e) => setF({ start_time: e.target.value })}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="time"
                  label={
                    form.block_kind === 'fixed'
                      ? tr('fieldEnd', 'Fin')
                      : tr('fieldWindowEnd', 'Fenêtre — à')
                  }
                  InputLabelProps={{ shrink: true }}
                  value={form.end_time}
                  onChange={(e) => setF({ end_time: e.target.value })}
                />
              </Stack>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  {tr('fieldDays', 'Jours')}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                  {WEEKDAYS.map((d) => (
                    <FormControlLabel
                      key={d}
                      control={
                        <Checkbox
                          size="small"
                          checked={!!form[d]}
                          onChange={(e) => setF({ [d]: e.target.checked })}
                        />
                      }
                      label={tr(`day.${d}`, d.slice(0, 3))}
                      sx={{ mr: 0.5 }}
                    />
                  ))}
                </Stack>
              </Box>

              <FormControlLabel
                control={
                  <Switch checked={!!form.active} onChange={(e) => setF({ active: e.target.checked })} />
                }
                label={tr('fieldActive', 'Active')}
              />
            </Stack>
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={26} />
            </Box>
          ) : (
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {tr(
                    'listHint',
                    'Ces contraintes nourrissent le générateur de planning. Définissez vos horaires de travail et vos habitudes.',
                  )}
                </Typography>
                <Button size="small" startIcon={<AddIcon />} variant="contained" onClick={openCreate}>
                  {tr('add', 'Ajouter')}
                </Button>
              </Stack>

              {items.length === 0 ? (
                <Alert
                  severity="info"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<AutoFixHighIcon />}
                      onClick={handleSeed}
                      disabled={saving}
                    >
                      {tr('seedBtn', 'Initialiser par défaut')}
                    </Button>
                  }
                >
                  {tr('empty', "Aucune contrainte. Partez d'un modèle prêt à l'emploi puis ajustez-le.")}
                </Alert>
              ) : (
                <TableContainer sx={{ maxHeight: 420, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{tr('colDomain', 'Domaine')}</TableCell>
                        <TableCell>{tr('colLabel', 'Bloc')}</TableCell>
                        <TableCell>{tr('colDays', 'Jours')}</TableCell>
                        <TableCell>{tr('colSlot', 'Créneau')}</TableCell>
                        <TableCell align="right">{tr('colActions', 'Actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((it) => (
                        <TableRow key={it.id} hover sx={!it.active ? { opacity: 0.5 } : undefined}>
                          <TableCell>
                            <Chip
                              size="small"
                              label={trDomain(it.domain)}
                              sx={{
                                bgcolor: DOMAIN_COLORS[it.domain] || DOMAIN_COLORS.other,
                                color: '#fff',
                                fontWeight: 700,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap title={it.label}>
                              {it.label}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{daysSummary(it)}</TableCell>
                          <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{slotSummary(it)}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Tooltip title={tr('edit', 'Modifier')}>
                              <IconButton size="small" onClick={() => openEdit(it)}>
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={tr('delete', 'Supprimer')}>
                              <IconButton size="small" onClick={() => handleDelete(it)}>
                                <DeleteOutlineIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {view === 'form' ? (
            <>
              <Button onClick={() => setView('list')} disabled={saving}>
                {tr('back', 'Retour')}
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {tr('save', 'Enregistrer')}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>{tr('close', 'Fermer')}</Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
})

ConstraintsModal.displayName = 'ConstraintsModal'

export default ConstraintsModal
